import React, { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, ExternalLink, Sun, Moon } from "lucide-react";
import { Logo } from "./Logo";
import { BIZ } from "../lib/constants";
import { useTheme } from "../lib/ThemeContext";

const NAV = [
    { to: "/", label: "Home" },
    {
        href: BIZ.shopUrl,
        label: "Shop",
        external: true,
    },
    { to: "/spirits", label: "Spirits" },
    { to: "/blog", label: "Blog" },
    { to: "/recipes", label: "Recipes" },
    { to: "/local/clinton-mo", label: "Local" },
    { to: "/reviews", label: "Reviews" },
    { to: "/about", label: "About" },
    { to: "/visit", label: "Visit" },
    { to: "/faq", label: "FAQ" },
    { to: "/contact", label: "Contact" },
];

export const Navbar = () => {
    const [scrolled, setScrolled] = useState(false);
    const [open, setOpen] = useState(false);
    const location = useLocation();
    const { theme, toggle } = useTheme();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 24);
        onScroll();
        window.addEventListener("scroll", onScroll, { passive: true });
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => {
        setOpen(false);
    }, [location.pathname]);

    // Theme-aware translucent nav background
    const navBg = scrolled || open
        ? (theme === "light" ? "rgba(250,247,242,0.92)" : "rgba(10,10,10,0.92)")
        : "transparent";

    // Bigger logo — top-of-page on home shows 80px, shrinks to 56px on scroll. Other pages always 56px.
    const isHomeTop = location.pathname === "/" && !scrolled;
    const logoSize = isHomeTop ? 80 : 56;

    return (
        <header
            className="navbar-base"
            data-testid="navbar"
            style={{
                position: "sticky",
                top: 0,
                zIndex: 50,
                backgroundColor: navBg,
                backdropFilter: scrolled || open ? "blur(16px)" : "none",
                borderBottom:
                    scrolled || open
                        ? "1px solid var(--ls-border)"
                        : "1px solid transparent",
            }}
        >
            <div className="ls-container">
                <div className="flex items-center justify-between" style={{ minHeight: 96, paddingTop: 12, paddingBottom: 12 }}>
                    <Link to="/" aria-label="Liquor Studio home" data-testid="nav-logo-link">
                        <Logo size={logoSize} />
                    </Link>

                    {/* Desktop */}
                    <nav
                        className="hidden lg:flex items-center gap-9"
                        aria-label="Primary"
                        style={{ whiteSpace: "nowrap" }}
                    >
                        {NAV.map((item) => {
                            if (item.external) {
                                return (
                                    <a
                                        key={item.label}
                                        href={item.href}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="link-underline text-white text-sm font-medium tracking-wide"
                                        style={{
                                            display: "inline-flex",
                                            alignItems: "center",
                                            gap: 4,
                                            whiteSpace: "nowrap",
                                        }}
                                        data-testid={`nav-link-shop`}
                                    >
                                        {item.label}
                                        <ExternalLink size={13} aria-hidden="true" />
                                    </a>
                                );
                            }
                            if (item.hash) {
                                return (
                                    <a
                                        key={item.label}
                                        href={item.to}
                                        className="link-underline text-white text-sm font-medium tracking-wide"
                                        data-testid={`nav-link-${item.label.toLowerCase()}`}
                                    >
                                        {item.label}
                                    </a>
                                );
                            }
                            if (item.disabled) {
                                return (
                                    <span
                                        key={item.label}
                                        className="text-sm font-medium tracking-wide"
                                        style={{ color: "var(--ls-text-muted)", cursor: "not-allowed" }}
                                        title="Coming soon"
                                        data-testid={`nav-link-${item.label.toLowerCase()}-disabled`}
                                    >
                                        {item.label}
                                    </span>
                                );
                            }
                            return (
                                <NavLink
                                    key={item.label}
                                    to={item.to}
                                    end={item.to === "/"}
                                    className={({ isActive }) =>
                                        `link-underline text-sm font-medium tracking-wide ${
                                            isActive
                                                ? "text-[color:var(--ls-gold)]"
                                                : "text-white"
                                        }`
                                    }
                                    data-testid={`nav-link-${item.label.toLowerCase()}`}
                                >
                                    {item.label}
                                </NavLink>
                            );
                        })}
                    </nav>

                    <div className="hidden lg:flex items-center gap-3">
                        <button
                            onClick={toggle}
                            aria-label={`Switch to ${theme === "light" ? "dark" : "light"} theme`}
                            className="btn-ghost"
                            style={{ padding: 10, borderRadius: 999, color: "var(--ls-text)" }}
                            data-testid="theme-toggle"
                            type="button"
                        >
                            {theme === "light" ? <Moon size={16} aria-hidden="true" /> : <Sun size={16} aria-hidden="true" />}
                        </button>
                        <a
                            href={BIZ.shopUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn-primary"
                            style={{ padding: "10px 20px", fontSize: 13 }}
                            data-testid="nav-shop-cta"
                        >
                            Shop Online <ExternalLink size={14} aria-hidden="true" />
                        </a>
                    </div>

                    {/* Mobile toggle */}
                    <div className="lg:hidden flex items-center gap-2">
                        <button
                            onClick={toggle}
                            aria-label={`Switch to ${theme === "light" ? "dark" : "light"} theme`}
                            style={{ background: "transparent", border: "none", color: "var(--ls-text)", padding: 8, cursor: "pointer" }}
                            data-testid="theme-toggle-mobile"
                            type="button"
                        >
                            {theme === "light" ? <Moon size={20} /> : <Sun size={20} />}
                        </button>
                        <button
                            onClick={() => setOpen((v) => !v)}
                            style={{ background: "transparent", border: "none", color: "var(--ls-text)", padding: 8, cursor: "pointer" }}
                            aria-label={open ? "Close menu" : "Open menu"}
                            aria-expanded={open}
                            data-testid="nav-mobile-toggle"
                        >
                            {open ? <X size={26} /> : <Menu size={26} />}
                        </button>
                    </div>
                </div>
            </div>

            {/* Mobile drawer */}
            {open && (
                <div
                    className="lg:hidden"
                    data-testid="nav-mobile-drawer"
                    style={{
                        background: "var(--ls-bg)",
                        borderTop: "1px solid var(--ls-border)",
                    }}
                >
                    <div className="ls-container py-6 flex flex-col gap-4">
                        {NAV.map((item) => {
                            if (item.external) {
                                return (
                                    <a
                                        key={item.label}
                                        href={item.href}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="text-white text-lg font-medium flex items-center gap-2"
                                        data-testid={`mnav-link-shop`}
                                    >
                                        {item.label} <ExternalLink size={16} />
                                    </a>
                                );
                            }
                            if (item.hash) {
                                return (
                                    <a
                                        key={item.label}
                                        href={item.to}
                                        className="text-white text-lg font-medium"
                                        data-testid={`mnav-link-${item.label.toLowerCase()}`}
                                    >
                                        {item.label}
                                    </a>
                                );
                            }
                            if (item.disabled) {
                                return (
                                    <span
                                        key={item.label}
                                        className="text-lg font-medium"
                                        style={{ color: "var(--ls-text-muted)" }}
                                    >
                                        {item.label} <em style={{ fontSize: 12 }}>(soon)</em>
                                    </span>
                                );
                            }
                            return (
                                <Link
                                    key={item.label}
                                    to={item.to}
                                    className="text-white text-lg font-medium"
                                    data-testid={`mnav-link-${item.label.toLowerCase()}`}
                                >
                                    {item.label}
                                </Link>
                            );
                        })}
                        <a
                            href={BIZ.shopUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn-primary mt-2 justify-center"
                            data-testid="mnav-shop-cta"
                        >
                            Shop Online <ExternalLink size={14} />
                        </a>
                    </div>
                </div>
            )}
        </header>
    );
};

export default Navbar;
